/**
 * useMessageQueue Hook
 * 
 * Manages message sending with offline support and retry functionality.
 * Features:
 * - When online: Send directly without showing pending bubble (instant)
 * - When offline: Queue message and show pending bubble
 * - Show pending bubble only if send takes longer than 2 seconds
 * - 40 second timeout before marking as failed
 * - Retry failed messages
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { sendTextMessage, sendImageMessage, sendVoiceMessage, sendVideoMessage, sendPollMessage } from '@/lib/firestore';
import { useOnlineStatus } from './useOnlineStatus';

// Timeout for showing error (40 seconds)
const SEND_TIMEOUT = 40000;

// Message status types
export const MESSAGE_STATUS = {
  SENDING: 'sending',
  SENT: 'sent',
  FAILED: 'failed'
};

export function useMessageQueue(conversationId, currentUser, onMessageSent) {
  const [pendingMessages, setPendingMessages] = useState([]);
  const isOnline = useOnlineStatus();
  const timeoutRefs = useRef({});

  // Clear pending messages when conversation changes
  useEffect(() => {
    setPendingMessages([]);
    // Clear any lingering timeouts from the previous conversation
    Object.values(timeoutRefs.current).forEach(timeout => clearTimeout(timeout));
    timeoutRefs.current = {};
  }, [conversationId]);

  // Clean up timeouts on unmount
  useEffect(() => {
    const refs = timeoutRefs.current;
    return () => {
      Object.values(refs).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Generate a temporary ID for optimistic updates
  const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

  // Add a pending message to the list (only called when needed)
  const addPendingMessage = useCallback((tempId, messageData) => {
    const pendingMessage = {
      ...messageData,
      id: tempId,
      tempId,
      status: MESSAGE_STATUS.SENDING,
      createdAt: new Date(),
      senderId: currentUser?.id,
      senderName: currentUser?.name || currentUser?.user_metadata?.name || currentUser?.user_metadata?.full_name || currentUser?.email
    };

    setPendingMessages(prev => {
      // Check if already exists (avoid duplicates)
      if (prev.some(msg => msg.tempId === tempId)) return prev;
      return [...prev, pendingMessage];
    });
  }, [currentUser]);

  // Update a pending message's status (and optionally the firestoreId)
  const updatePendingStatus = useCallback((tempId, status, error = null, firestoreId = null) => {
    setPendingMessages(prev => 
      prev.map(msg => 
        msg.tempId === tempId 
          ? { ...msg, status, error, ...(firestoreId ? { firestoreId } : {}) } 
          : msg
      )
    );
  }, []);

  // Remove a pending message (after successful send or cancel)
  const removePendingMessage = useCallback((tempId) => {
    setPendingMessages(prev => prev.filter(msg => msg.tempId !== tempId));
    
    // Clear all timeouts for this message
    if (timeoutRefs.current[tempId]) {
      clearTimeout(timeoutRefs.current[tempId]);
      delete timeoutRefs.current[tempId];
    }
  }, []);

  // Send a message - show pending bubble immediately for instant feedback
  const sendWithSmartPending = useCallback(async (sendFn, tempId, messageData) => {
    // Show pending bubble immediately (optimistic rendering)
    addPendingMessage(tempId, messageData);

    // If offline, mark as failed immediately
    if (!navigator.onLine) {
      updatePendingStatus(tempId, MESSAGE_STATUS.FAILED, 'No connection');
      return { success: false, error: 'No connection' };
    }

    // Set timeout to mark as failed after 40 seconds
    timeoutRefs.current[tempId] = setTimeout(() => {
      updatePendingStatus(tempId, MESSAGE_STATUS.FAILED, 'No connection');
    }, SEND_TIMEOUT);

    try {
      const result = await sendFn();
      
      // Clear timeout on success
      if (timeoutRefs.current[tempId]) {
        clearTimeout(timeoutRefs.current[tempId]);
        delete timeoutRefs.current[tempId];
      }

      // Don't remove the pending message yet — keep it visible until the
      // real Firestore message arrives via onSnapshot. Store the Firestore
      // doc ID so MessageView can dedup (same-frame swap, zero layout shift).
      updatePendingStatus(tempId, MESSAGE_STATUS.SENT, null, result?.id || null);

      // Safety net: remove after 5s even if onSnapshot hasn't confirmed
      timeoutRefs.current[`cleanup_${tempId}`] = setTimeout(() => {
        removePendingMessage(tempId);
        delete timeoutRefs.current[`cleanup_${tempId}`];
      }, 5000);
      
      // Notify parent
      if (onMessageSent) {
        onMessageSent(result);
      }

      return { success: true, message: result };
    } catch (error) {
      if (timeoutRefs.current[tempId]) {
        clearTimeout(timeoutRefs.current[tempId]);
        delete timeoutRefs.current[tempId];
      }

      console.error('Error sending message:', error);
      
      // Update pending bubble to failed state
      updatePendingStatus(tempId, MESSAGE_STATUS.FAILED, error.message || 'Failed to send');
      
      return { success: false, error };
    }
  }, [addPendingMessage, updatePendingStatus, removePendingMessage, onMessageSent]);

  // Send a text message
  const sendText = useCallback(async (text, replyTo = null) => {
    if (!conversationId || !currentUser?.id) return { success: false };

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    const tempId = generateTempId();
    const messageData = {
      type: 'text',
      text,
      replyTo,
      sender
    };

    return sendWithSmartPending(
      () => sendTextMessage(conversationId, sender, text, replyTo),
      tempId,
      messageData
    );
  }, [conversationId, currentUser, sendWithSmartPending]);

  // Send an image message
  const sendImages = useCallback(async (images, caption = '', replyTo = null) => {
    if (!conversationId || !currentUser?.id) return { success: false };

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    const tempId = generateTempId();
    const messageData = {
      type: 'image',
      text: caption,
      images,
      replyTo,
      sender
    };

    return sendWithSmartPending(
      () => sendImageMessage(conversationId, sender, images, caption, replyTo),
      tempId,
      messageData
    );
  }, [conversationId, currentUser, sendWithSmartPending]);

  // Send a voice message
  const sendVoice = useCallback(async (voiceMemo, _unused, replyTo = null) => {
    if (!conversationId || !currentUser?.id) return { success: false };

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    const tempId = generateTempId();
    const messageData = {
      type: 'voice',
      voiceMemo,
      replyTo,
      sender
    };

    return sendWithSmartPending(
      () => sendVoiceMessage(conversationId, sender, voiceMemo, replyTo),
      tempId,
      messageData
    );
  }, [conversationId, currentUser, sendWithSmartPending]);

  // Send a video message
  const sendVideo = useCallback(async (video, caption = '', replyTo = null) => {
    if (!conversationId || !currentUser?.id) return { success: false };

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    const tempId = generateTempId();
    const messageData = {
      type: 'video',
      video,
      text: caption,
      replyTo,
      sender
    };

    return sendWithSmartPending(
      () => sendVideoMessage(conversationId, sender, video, caption, replyTo),
      tempId,
      messageData
    );
  }, [conversationId, currentUser, sendWithSmartPending]);

  const sendPoll = useCallback(async (poll, replyTo = null) => {
    if (!conversationId || !currentUser?.id) return { success: false };

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    const tempId = generateTempId();
    const messageData = {
      type: 'poll',
      poll,
      text: '',
      replyTo,
      sender
    };

    return sendWithSmartPending(
      () => sendPollMessage(conversationId, sender, poll, replyTo),
      tempId,
      messageData
    );
  }, [conversationId, currentUser, sendWithSmartPending]);


  // Retry a failed message
  const retryMessage = useCallback(async (tempId) => {
    const message = pendingMessages.find(msg => msg.tempId === tempId);
    if (!message) return { success: false };

    // Update status back to sending
    updatePendingStatus(tempId, MESSAGE_STATUS.SENDING, null);

    const sender = {
      id: currentUser.id,
      name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email,
      email: currentUser.email,
      avatar: currentUser.avatar || null,
      church_id: currentUser.church_id || null
    };

    // Set timeout to mark as failed after 40 seconds
    timeoutRefs.current[tempId] = setTimeout(() => {
      updatePendingStatus(tempId, MESSAGE_STATUS.FAILED, 'No connection');
    }, SEND_TIMEOUT);

    try {
      let result;
      
      // Retry based on message type
      switch (message.type) {
        case 'text':
          result = await sendTextMessage(conversationId, sender, message.text, message.replyTo);
          break;
        case 'image':
          result = await sendImageMessage(conversationId, sender, message.images, message.text, message.replyTo);
          break;
        case 'voice':
          result = await sendVoiceMessage(conversationId, sender, message.voiceMemo, message.replyTo);
          break;
        case 'video':
          result = await sendVideoMessage(conversationId, sender, message.video, message.text, message.replyTo);
          break;
        case 'poll':
          result = await sendPollMessage(conversationId, sender, message.poll, message.replyTo);
          break;
        default:
          throw new Error('Unknown message type');
      }

      // Clear timeout on success
      if (timeoutRefs.current[tempId]) {
        clearTimeout(timeoutRefs.current[tempId]);
        delete timeoutRefs.current[tempId];
      }

      // Keep pending visible until Firestore confirms, same as initial send
      updatePendingStatus(tempId, MESSAGE_STATUS.SENT, null, result?.id || null);

      // Safety net: remove after 5s
      timeoutRefs.current[`cleanup_${tempId}`] = setTimeout(() => {
        removePendingMessage(tempId);
        delete timeoutRefs.current[`cleanup_${tempId}`];
      }, 5000);
      
      // Notify parent
      if (onMessageSent) {
        onMessageSent(result);
      }

      return { success: true, message: result };
    } catch (error) {
      // Clear timeout on error
      if (timeoutRefs.current[tempId]) {
        clearTimeout(timeoutRefs.current[tempId]);
        delete timeoutRefs.current[tempId];
      }

      console.error('Error retrying message:', error);
      updatePendingStatus(tempId, MESSAGE_STATUS.FAILED, error.message || 'Failed to send');
      return { success: false, error };
    }
  }, [pendingMessages, conversationId, currentUser, updatePendingStatus, removePendingMessage, onMessageSent]);

  // Cancel/remove a failed message
  const cancelMessage = useCallback((tempId) => {
    removePendingMessage(tempId);
  }, [removePendingMessage]);

  return {
    pendingMessages,
    isOnline,
    sendText,
    sendImages,
    sendVoice,
    sendVideo,
    sendPoll,
    retryMessage,
    cancelMessage
  };
}
